import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, Image as ImageIcon, Download, Send, RefreshCw, Wand2, Copy, CheckCircle, Upload, X, Hash, Zap, Brain, Layers } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminFetch } from '../lib/api';
import { LearningContextBox } from '../components/marketing/LearningContextBox';
import { VariantSelector } from '../components/marketing/VariantSelector';
import { HookOptimizer } from '../components/marketing/HookOptimizer';

interface DBTemplate {
  id: string;
  name: string;
  category: string;
  prompt: string;
  hashtag_prompt: string | null;
  is_system: boolean;
  is_active: boolean;
}

const CATEGORY_EMOJI: Record<string, string> = {
  urgency: '🔥', social_proof: '⭐', pain_point: '😰', action: '🎯',
  transformation: '🦋', tip: '💡', quiz: '❓', fun_fact: '🤯',
  seasonal: '🌸', motivation: '💪', custom: '✏️',
};

export const MarketingCreatePage: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [template, setTemplate] = useState('custom');
  const [templates, setTemplates] = useState<DBTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState('');
  const [regenerateImage, setRegenerateImage] = useState(false);
  const [post, setPost] = useState<any>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState('');
  const [referenceImage, setReferenceImage] = useState<string>('');
  const [referencePreview, setReferencePreview] = useState<string>('');
  const [hashtagPrompt, setHashtagPrompt] = useState<string>('');
  const [editingPostId, setEditingPostId] = useState<string>('');
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);

  // New Phase 1 state
  const [learnings, setLearnings] = useState<any[]>([]);
  const [learningsLoading, setLearningsLoading] = useState(false);
  const [variants, setVariants] = useState<any[]>([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<number | undefined>();
  const [hooks, setHooks] = useState<string[]>([]);
  const [hooksLoading, setHooksLoading] = useState(false);

  // Load templates from DB
  useEffect(() => {
    loadTemplates();
    loadLearningContext();
  }, []);

  const loadTemplates = async () => {
    try {
      const resp = await adminFetch(`/api/admin/marketing/templates`, {
              });
      if (resp.ok) {
        const data = await resp.json();
        setTemplates(data.filter((t: DBTemplate) => t.is_active));
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const loadLearningContext = async () => {
    try {
      setLearningsLoading(true);
      const resp = await adminFetch(`/api/admin/marketing/learning-context?task=generatePost`, {
              });
      if (resp.ok) {
        const data = await resp.json();
        setLearnings(data.learnings || []);
      }
    } catch (err) {
      console.error('Failed to load learning context:', err);
    } finally {
      setLearningsLoading(false);
    }
  };

  // Load post for editing (from URL param)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get('edit');
    if (editId) loadPostForEdit(editId);
  }, []);

  const loadPostForEdit = async (postId: string) => {
    try {
      const resp = await adminFetch(`/api/admin/marketing/posts`, {
              });
      const posts = await resp.json();
      const found = Array.isArray(posts) ? posts.find((p: any) => p.id === postId) : null;
      if (found) {
        setPost(found);
        setEditingPostId(found.id);
        setPrompt(found.prompt_history?.[0]?.prompt || '');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError('');
    setVariants([]);
    setSelectedVariant(undefined);
    setHooks([]);
    setStatus('Caption wird generiert...');
    try {
      const selectedTemplate = templates.find(t => t.category === template);
      const resp = await adminFetch(`/api/admin/marketing/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          templateType: template !== 'custom' ? template : undefined,
          referenceImage: referenceImage || undefined,
          hashtagPrompt: hashtagPrompt || (selectedTemplate?.hashtag_prompt) || undefined,
          selectedAssetIds: selectedAssetIds.length > 0 ? selectedAssetIds : undefined,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setPost(data.post);
        setEditingPostId(data.post.id);
        setStatus('');
        if (data.warnings?.length > 0) {
          setStatus(`⚠️ ${data.warnings.join(' | ')}`);
          setTimeout(() => setStatus(''), 5000);
        }
      } else {
        const details = data.details?.map((d: any) => `${d.field}: ${d.message}`).join(', ');
        setError(details ? `${data.error}: ${details}` : (data.error || 'Fehler bei der Generierung'));
        setStatus('');
      }
    } catch (err: any) {
      setError(err.message);
      setStatus('');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateVariants = async () => {
    if (!prompt.trim()) return;
    setVariantsLoading(true);
    setVariants([]);
    setSelectedVariant(undefined);
    try {
      const resp = await adminFetch(`/api/admin/marketing/variants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, variantCount: 3, audience: [] }),
      });
      const data = await resp.json();
      if (data.success) setVariants(data.variants || []);
    } catch (err) {
      console.error('Variants error:', err);
    } finally {
      setVariantsLoading(false);
    }
  };

  const handleOptimizeHook = async () => {
    if (!post?.caption) return;
    setHooksLoading(true);
    setHooks([]);
    try {
      const firstLine = post.caption.split('\n')[0];
      const resp = await adminFetch(`/api/admin/marketing/hook-optimize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentHook: firstLine, captionExcerpt: post.caption }),
      });
      const data = await resp.json();
      if (data.success) setHooks(data.hooks || []);
    } catch (err) {
      console.error('Hook optimize error:', err);
    } finally {
      setHooksLoading(false);
    }
  };

  const handleSelectHook = (hook: string) => {
    if (!post?.caption) return;
    const lines = post.caption.split('\n');
    lines[0] = hook;
    setPost((p: any) => ({ ...p, caption: lines.join('\n') }));
  };

  const handleSelectVariant = (variant: any) => {
    setSelectedVariant(variants.indexOf(variant));
    setPost((prev: any) => prev ? {
      ...prev,
      caption: variant.caption,
      hashtags: variant.hashtags,
      image_url: variant.imageUrl || prev.image_url,
      image_prompt: variant.imagePrompt || prev.image_prompt,
    } : {
      caption: variant.caption,
      hashtags: variant.hashtags,
      image_url: variant.imageUrl || null,
      image_prompt: variant.imagePrompt || '',
      title: 'Aus Variante erstellt',
      status: 'draft',
    });
  };

  const handleRefine = async () => {
    if (!refinePrompt.trim() || !post) return;
    setRefining(true);
    setError('');
    setStatus('Post wird angepasst...');
    try {
      const resp = await adminFetch(`/api/admin/marketing/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postId: post.id, prompt: refinePrompt, regenerateImage }),
      });
      const data = await resp.json();
      if (data.success) {
        setPost(data.post);
        setRefinePrompt('');
        setStatus('');
      } else {
        setError(data.error || 'Fehler');
        setStatus('');
      }
    } catch (err: any) {
      setError(err.message);
      setStatus('');
    } finally {
      setRefining(false);
    }
  };

  const handleCopyCaption = () => {
    if (!post) return;
    const text = `${post.caption}\n\n${(post.hashtags || []).join(' ')}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadImage = () => {
    if (!post?.image_url) return;
    const link = document.createElement('a');
    link.href = post.image_url;
    link.download = `fahryo_post_${post.id?.slice(0, 8)}.png`;
    link.target = '_blank';
    link.click();
  };

  const handleSave = async () => {
    if (!post) return;
    try {
      await adminFetch(`/api/admin/marketing/posts/${post.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption: post.caption, hashtags: post.hashtags, status: post.status || 'draft' }),
      });
      setStatus('Gespeichert ✅');
      setTimeout(() => setStatus(''), 2000);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1><Sparkles size={24} /> {editingPostId ? 'Post bearbeiten' : 'Post erstellen'}</h1>
        {post && (
          <button className="btn btn-secondary" onClick={() => { setPost(null); setPrompt(''); setEditingPostId(''); setHashtagPrompt(''); setVariants([]); setHooks([]); window.history.replaceState({}, '', '/marketing/create'); }}>
            <RefreshCw size={14} /> Neuer Post
          </button>
        )}
      </div>

      {!post ? (
        /* ===== GENERATION FORM ===== */
        <div>
          {/* Learning Context */}
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <LearningContextBox learnings={learnings} isLoading={learningsLoading} />
          </div>

          {/* Template selection from DB */}
          <div className="card mb-4" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 12 }}>Vorlage wählen</h3>
            {templatesLoading ? (
              <div className="flex gap-2">
                {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ width: 150, height: 60, borderRadius: 8 }} />)}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => {
                      setTemplate(t.category);
                      setPrompt(t.prompt);
                      setHashtagPrompt(t.hashtag_prompt || '');
                    }}
                    className="card"
                    style={{
                      padding: '12px 16px', textAlign: 'left', cursor: 'pointer',
                      border: template === t.category ? '2px solid var(--primary)' : '1px solid var(--border)',
                      background: template === t.category ? 'rgba(224,55,5,0.08)' : undefined,
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {CATEGORY_EMOJI[t.category] || '📝'} {t.name}
                    </div>
                    <div className="text-xs text-muted">{t.category}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Prompt input */}
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 12 }}>Prompt</h3>
            <textarea
              className="input"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder={"Beschreibe den Instagram-Post, den du erstellen möchtest...\n\nBeispiel: Erstelle einen Post über die Gefahren von Aquaplaning im Herbst mit Tipps für Fahranfänger."}
              rows={5}
              style={{ resize: 'vertical', fontFamily: 'inherit', width: '100%' }}
            />

            {/* Hashtag Prompt */}
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)' }}>
              <div className="flex gap-2 items-center mb-1">
                <Hash size={14} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>Hashtag-Prompt</span>
                <span className="text-xs text-muted">(max. 5 Hashtags — Instagram 2026)</span>
              </div>
              <textarea
                className="input"
                value={hashtagPrompt}
                onChange={e => setHashtagPrompt(e.target.value)}
                placeholder="z.B. 'Erstelle 5 Hashtags: 2 High-Volume + 2 Nische + 1 Branded (#Fahryo)'"
                rows={2}
                style={{ resize: 'vertical', fontFamily: 'inherit', width: '100%', fontSize: 12 }}
              />
            </div>

            {error && <div style={{ color: 'var(--danger)', marginTop: 8, fontSize: 13 }}>{error}</div>}

            {/* Reference Image & Brand Assets */}
            <div style={{ marginTop: 14, padding: 14, borderRadius: 10, border: '1px dashed var(--border)', background: 'rgba(255,255,255,0.02)' }}>
              <div className="flex gap-2 items-center mb-2">
                <ImageIcon size={16} />
                <span style={{ fontWeight: 600, fontSize: 13 }}>Referenzbild / Brand Assets</span>
                <span className="text-xs text-muted">(optional)</span>
              </div>
              <p className="text-xs text-muted mb-2">Wähle Brand Assets als Referenz oder lade ein eigenes Bild hoch. Die KI nutzt diese als visuelle Vorlage.</p>

              {/* Brand Assets Picker */}
              <BrandAssetPicker selectedIds={selectedAssetIds} onToggle={(id) => {
                setSelectedAssetIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
              }} />

              {/* Manual Upload */}
              <div className="mt-2">
                <span className="text-xs text-muted" style={{ fontWeight: 600 }}>Oder eigenes Bild hochladen:</span>
                <div className="mt-1">
                {referencePreview ? (
                  <div className="flex gap-3 items-start">
                    <div style={{ position: 'relative' }}>
                      <img src={referencePreview} alt="Referenz" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                      <button onClick={() => { setReferenceImage(''); setReferencePreview(''); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'var(--danger)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                    </div>
                    <div className="text-xs text-muted" style={{ paddingTop: 4 }}>Eigenes Referenzbild ✅</div>
                  </div>
                ) : (
                  <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer', display: 'inline-flex', fontSize: 12 }}>
                    <Upload size={12} /> Bild hochladen
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = () => {
                          const base64 = reader.result as string;
                          setReferenceImage(base64);
                          setReferencePreview(base64);
                        };
                        reader.readAsDataURL(file);
                      }
                      e.target.value = '';
                    }} />
                  </label>
                )}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              <button className="btn btn-primary" onClick={handleGenerate} disabled={generating || !prompt.trim()}>
                {generating ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={16} />}
                {generating ? status || 'Generiere...' : 'Post generieren'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleGenerateVariants}
                disabled={variantsLoading || !prompt.trim()}
                title="Erstelle 3 verschiedene Varianten zum Vergleichen"
              >
                {variantsLoading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Layers size={14} />}
                3 Varianten
              </button>
            </div>
          </div>

          {/* Variants Section */}
          {(variantsLoading || variants.length > 0) && (
            <div className="card mt-4" style={{ padding: 20 }}>
              {variantsLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', color: '#8B5CF6' }} />
                  <span style={{ fontSize: 13, color: '#8B5CF6' }}>Varianten + Bilder werden generiert...</span>
                </div>
              ) : (
                <VariantSelector
                  variants={variants}
                  onSelect={handleSelectVariant}
                  selectedIndex={selectedVariant}
                />
              )}
            </div>
          )}
        </div>
      ) : (
        /* ===== POST EDITOR & PREVIEW ===== */
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

          {/* Instagram Preview */}
          <div>
            <div className="card" style={{ padding: 0, overflow: 'hidden', maxWidth: 400 }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #E03705, #FF6B35)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14 }}>F</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>fahryo.de</div>
                  <div className="text-xs text-muted">Fahryo • Führerschein-App</div>
                </div>
              </div>

              {/* Image */}
              {post.image_url ? (
                <img src={post.image_url} alt="Post" style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', aspectRatio: '1/1', background: 'linear-gradient(135deg, #E03705 0%, #FF6B35 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexDirection: 'column', gap: 8 }}>
                  <ImageIcon size={48} />
                  <span style={{ fontSize: 12, opacity: 0.8 }}>Bild wird bei nächster Generierung erstellt</span>
                </div>
              )}

              {/* Actions */}
              <div style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                  <span>❤️</span><span>💬</span><span>📤</span>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                  <strong>fahryo.de</strong> {post.caption}
                </div>
                <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 8 }}>
                  {(post.hashtags || []).join(' ')}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 mt-3">
              <button className="btn btn-secondary" onClick={handleCopyCaption}>
                {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
                {copied ? 'Kopiert!' : 'Caption kopieren'}
              </button>
              {post.image_url && (
                <button className="btn btn-secondary" onClick={handleDownloadImage}>
                  <Download size={14} /> Bild laden
                </button>
              )}
            </div>

            {/* Hook Optimizer (below preview) */}
            <div style={{ marginTop: 'var(--space-4)' }}>
              {!hooksLoading && hooks.length === 0 && post.caption && (
                <button
                  className="btn btn-secondary"
                  onClick={handleOptimizeHook}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  <Zap size={14} /> Hook optimieren
                </button>
              )}
              <HookOptimizer
                hooks={hooks}
                isLoading={hooksLoading}
                onSelectHook={handleSelectHook}
                currentHook={post.caption?.split('\n')[0]}
              />
            </div>
          </div>

          {/* Editor Panel */}
          <div>
            {/* Caption editor */}
            <div className="card mb-3" style={{ padding: 16 }}>
              <div className="flex justify-between items-center mb-2">
                <h3 style={{ margin: 0 }}>Caption</h3>
                <span className="text-xs text-muted">{post.caption?.length || 0} / 2.200</span>
              </div>
              <textarea
                className="input"
                value={post.caption || ''}
                onChange={e => setPost((p: any) => ({ ...p, caption: e.target.value }))}
                rows={8}
                style={{ resize: 'vertical', fontFamily: 'inherit', width: '100%', fontSize: 13 }}
              />
            </div>

            {/* Hashtags */}
            <div className="card mb-3" style={{ padding: 16 }}>
              <div className="flex justify-between items-center mb-2">
                <h3 style={{ margin: 0 }}>Hashtags</h3>
                <span className="text-xs text-muted">{(post.hashtags || []).length} / 5</span>
              </div>
              <input
                className="input"
                value={(post.hashtags || []).join(' ')}
                onChange={e => setPost((p: any) => ({ ...p, hashtags: e.target.value.split(' ').filter(Boolean) }))}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              {(post.hashtags || []).length > 5 && (
                <div className="text-xs mt-1" style={{ color: 'var(--danger)' }}>⚠️ Instagram 2026 erlaubt max. 5 Hashtags</div>
              )}
            </div>

            {/* Refine with follow-up */}
            <div className="card mb-3" style={{ padding: 16, border: '1px solid var(--primary)', borderStyle: 'dashed' }}>
              <h3 style={{ marginBottom: 8 }}>
                <Wand2 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Post anpassen
              </h3>
              <textarea
                className="input"
                value={refinePrompt}
                onChange={e => setRefinePrompt(e.target.value)}
                placeholder="z.B. 'Mache den Text kürzer und füge mehr Emojis ein' oder 'Wechsle das Thema zu Winterreifen'"
                rows={3}
                style={{ resize: 'vertical', fontFamily: 'inherit', width: '100%', fontSize: 13 }}
              />
              <div className="flex gap-2 items-center mt-2">
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={regenerateImage} onChange={e => setRegenerateImage(e.target.checked)} />
                  Auch Bild neu generieren
                </label>
                <div style={{ flex: 1 }} />
                <button className="btn btn-primary" onClick={handleRefine} disabled={refining || !refinePrompt.trim()}>
                  {refining ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} />}
                  Anpassen
                </button>
              </div>
            </div>

            {/* Save */}
            <div className="card" style={{ padding: 16 }}>
              <div className="flex gap-2">
                <button className="btn btn-primary" onClick={handleSave}>
                  <CheckCircle size={14} /> Speichern
                </button>
                <button className="btn btn-secondary" onClick={() => { setPost(null); setPrompt(''); setEditingPostId(''); setHashtagPrompt(''); setVariants([]); setHooks([]); window.history.replaceState({}, '', '/marketing/create'); }}>
                  <RefreshCw size={14} /> Neuer Post
                </button>
              </div>
              {status && <div className="text-sm mt-2" style={{ color: 'var(--success)' }}>{status}</div>}
            </div>

            {/* Learning Context (in editor) */}
            <div style={{ marginTop: 'var(--space-3)' }}>
              <LearningContextBox learnings={learnings} isLoading={learningsLoading} />
            </div>

            {/* Prompt History */}
            {post.prompt_history?.length > 0 && (
              <div className="card mt-3" style={{ padding: 16 }}>
                <h4 className="text-xs text-muted mb-2">Prompt-Verlauf</h4>
                {post.prompt_history.map((h: any, i: number) => (
                  <div key={i} className="text-xs" style={{ padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                    <span className="text-muted">{new Date(h.timestamp).toLocaleTimeString('de-DE')}:</span> {h.prompt}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─── Brand Asset Picker (inline mini-component) ──────────────────
interface BrandAssetPickerProps { selectedIds: string[]; onToggle: (id: string) => void; }
interface PickerAsset { id: string; type: string; label: string; url: string; }

const BrandAssetPicker: React.FC<BrandAssetPickerProps> = ({ selectedIds, onToggle }) => {
  const [assets, setAssets] = useState<PickerAsset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token;
        const resp = await adminFetch(`/api/admin/marketing/assets`, {
                  });
        const data = await resp.json();
        setAssets(data.assets || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-xs text-muted">Assets laden...</div>;
  if (assets.length === 0) return (
    <div className="text-xs text-muted" style={{ padding: '6px 0' }}>
      Keine Brand Assets vorhanden. <a href="/marketing/branding" style={{ color: '#E03705', textDecoration: 'underline' }}>Jetzt hochladen →</a>
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
      {assets.map(asset => {
        const isSelected = selectedIds.includes(asset.id);
        return (
          <button key={asset.id} onClick={() => onToggle(asset.id)}
            style={{
              position: 'relative', width: 64, height: 64, borderRadius: 8, overflow: 'hidden',
              border: isSelected ? '2px solid #E03705' : '2px solid transparent',
              opacity: isSelected ? 1 : 0.6, cursor: 'pointer', padding: 0, background: 'none',
              transition: 'all 0.15s ease',
            }}>
            <img src={asset.url} alt={asset.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            {isSelected && (
              <div style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', background: '#E03705', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>✓</span>
              </div>
            )}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', padding: '2px 4px' }}>
              <div style={{ color: '#fff', fontSize: 8, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{asset.label}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
};
